import {
  Img,
  Box,
  Flex,
  Text,
  Grid,
  Link,
  Stack,
  HStack,
  VStack,
  Accordion,
} from '@chakra-ui/react';
import { forwardRef, memo } from 'react';

import Wrapper from 'shared/Wrapper';
import NextImage from 'shared/NextImage';
import OfficeInfo from 'shared/OfficeInfo';
import AccordionInfo from 'shared/AccordionInfo';
import CustomOverlayLink from 'shared/CustomOverlayLink';
import PopupBlockedSocials from 'shared/PopupBlockedSocials';

import { infoLinks, offices, socialMedias } from 'constants/footer';
import DownAngle from 'assets/icons/DownAngle.svg';
import commonAssets from 'assets/common';

const Footer = (props, ref) => (
  <VStack
    ref={ref}
    as="footer"
    id="footer"
    w="full"
    gap="0"
    bgColor="bg.black"
    borderTopRadius={{ mobile: '32px', tablet: '48px', laptop: '56px' }}
    pos="relative"
    zIndex="900"
    boxSizing="border-box"
    color="bg.grayLight"
    pt={{ mobile: '32px', tablet: '52px', laptop: '100px' }}
    px={{ laptop: '52px', tablet: '27px', mobile: '24px' }}
    {...props}
  >
    <Wrapper
      gap={{ laptop: '100px', mobile: '40px' }}
      mb={{ laptop: '100px', mobile: '40px' }}
      mt="0"
      pt="0"
      pb="0"
      px={{ laptop: '48px', tablet: '25px', mobile: '0px' }}
    >
      <Stack
        direction={{ tablet: 'row', mobile: 'column' }}
        w="full"
        justify="space-between"
        gap={{ mobile: '32px', tablet: '15px' }}
      >
        <Text as="h2" color="text.white">
          Наши офисы
        </Text>

        <VStack
          justify="end"
          align="start"
          gap={{ mobile: '16px', tablet: '12px', laptop: '24px' }}
        >
          {infoLinks.map(({ type, icon, text, linkTitle, link, linkProps = null }) => (
            <HStack
              key={`infoLink-${type}`}
              whiteSpace="nowrap"
              gap={{ laptop: '16px', mobile: '8px' }}
            >
              <Img
                src={icon?.src ? icon.src : icon}
                alt={type}
                color="text.white"
                alignItems="center"
                filter="brightness(0) invert(1)"
                boxSize={{ mobile: '24px', tablet: '20px', laptop: '24px' }}
              />

              <Text
                pos="relative"
                textStyle="h1"
                fontWeight="500"
                fontSize={{ desktop: '24px', laptop: '20px', tablet: '16px', mobile: '12px' }}
                textUnderlineOffset="0.25em"
                textDecoration={type === 'Реквизиты' && 'underline'}
                _after={type === 'Реквизиты' && { content: `'.'`, color: 'bg.accent' }}
              >
                <CustomOverlayLink title={linkTitle} link={link} {...linkProps} />
                {text}
              </Text>
            </HStack>
          ))}

          <Flex w="full" pos="relative" flexWrap="nowrap" gap={{ mobile: '18px', laptop: '32px' }}>
            {socialMedias.map(({ src, href, alt, isBlocked = false }, i) =>
              isBlocked ? (
                <PopupBlockedSocials key={i}>
                  <Link href={href} isExternal>
                    <NextImage
                      src={src}
                      alt={alt}
                      w="auto"
                      h="auto"
                      boxSize={{
                        mobile: '24px',
                        tablet: '28px',
                        laptop: '32px',
                        desktop: '40px',
                      }}
                    />
                  </Link>
                </PopupBlockedSocials>
              ) : (
                <Link key={i} href={href} isExternal>
                  <NextImage
                    src={src}
                    alt={alt}
                    w="auto"
                    h="auto"
                    boxSize={{ mobile: '24px', tablet: '28px', laptop: '32px', desktop: '40px' }}
                  />
                </Link>
              ),
            )}
          </Flex>
        </VStack>
      </Stack>

      <VStack w="full" align="start" gap={{ laptop: '56px', mobile: '24px' }}>
        <Grid
          w="full"
          justifyContent="space-between"
          templateColumns={{
            laptop: 'repeat(3, minmax(auto, 346px))',
            xxm: 'repeat(3, minmax(auto, 187px))',
            mobile: 'repeat(1, auto)',
          }}
          columnGap={{ laptop: '101px', mobile: '32px' }}
          rowGap={{ laptop: '48px', xxm: '24px' }}
          hideBelow="xxm"
        >
          {offices?.map(val => (
            <OfficeInfo key={val.city} {...val} />
          ))}
        </Grid>

        <Accordion
          allowToggle
          w="full"
          flex="1"
          gap="21px"
          display="flex"
          flexDirection="column"
          hideFrom="xxm"
        >
          {offices?.map(({ city, address }, i) => (
            <AccordionInfo
              key={i}
              title={city}
              content={address}
              panelStyle={{ textStyle: 'h2', color: '#A7A9A5', ps: '20px', pt: '12px' }}
              leftIcon={
                <Box borderRadius="full" placeSelf="center" boxSize="8px" bgColor="bg.accent" />
              }
              rightIcon={DownAngle}
              rightIconProps={{ filter: 'brightness(0) invert(1)' }}
              titleSx={{ fontWeight: 500 }}
              contentSx={{ fontWeight: 500 }}
            />
          ))}
        </Accordion>
      </VStack>
    </Wrapper>

    <Box w="full" maxW={{ laptop: '1390px' }}>
      <Img src={commonAssets.logo.light} alt="Logo" w="full" h="auto" />
    </Box>
  </VStack>
);

export default memo(forwardRef(Footer));
